/**
 * Curated mapping from user-facing "domain" presets to the actual
 * category tag strings stored on `precedents.categories`.
 *
 * The backend (`pipeline/api.py::SearchRequest.categories`) does an
 * OR-match: a chunk passes the filter if its precedent's categories
 * array shares ANY tag with the request.  So a preset like
 * `capital_markets` simply expands to the union of all its child tags
 * before being sent on the wire.
 *
 * Why presets instead of exposing every raw tag?  We have ~35 distinct
 * tags in the corpus (a mix of Chinese and English, some only used by
 * a single precedent).  Showing them all as 35 toggles overwhelms the
 * sidebar and hides the high-signal groupings (e.g. that
 * `capital_markets`, `securities` and `amendment_act` are really one
 * domain).  Presets give us a stable 8-cell grid that maps cleanly to
 * how lawyers actually scope their research.
 *
 * To add or rebalance presets:
 *   1. Re-run the corpus inspector
 *      (`SELECT categories FROM precedents`) to see fresh counts.
 *   2. Edit the table below — keys here are also i18n keys
 *      (see `src/lib/i18n.tsx::categoryPreset*`).
 *   3. Restart `vite`; no backend change required.
 */
export interface CategoryPreset {
  /** Stable identifier — also used as i18n key (`categoryPreset.<key>`). */
  key: string
  /** OR-expansion sent to the backend as `categories: [...]`. */
  tags: string[]
}

export const CATEGORY_PRESETS: readonly CategoryPreset[] = [
  {
    key: 'capital_markets',
    tags: [
      'capital_markets',
      'securities',
      'amendment_act',
      'subsidiary_legislation',
      'SRO',
      'schedule_amendment',
      'fees',
      'regulations',
    ],
  },
  {
    key: 'financial_regulation',
    tags: [
      '金融监管',
      '数字货币',
      '货币服务',
      '反洗钱',
      '监管政策',
      '伊斯兰金融',
    ],
  },
  {
    key: 'financial_disputes',
    tags: ['金融纠纷', '信托', '不当得利', '商业'],
  },
  {
    key: 'family',
    tags: ['婚姻纠纷', '家事法庭', '心理评估'],
  },
  {
    key: 'constitution',
    tags: ['宪法', '基本法', '马来文译本'],
  },
  {
    key: 'criminal',
    tags: ['刑事', '反欺凌', '法律援助', '司法行政'],
  },
  {
    key: 'academic',
    tags: ['学术文章'],
  },
  {
    key: 'misc_statutes',
    tags: ['法令', '破产法', '跨境破产', '税务', '财政', '拍卖'],
  },
] as const

/** Convenience: flatten the selected presets into the wire payload. */
export function expandPresetsToTags(selectedKeys: string[]): string[] {
  if (selectedKeys.length === 0) return []
  const out = new Set<string>()
  for (const k of selectedKeys) {
    const preset = CATEGORY_PRESETS.find((p) => p.key === k)
    if (!preset) continue
    for (const t of preset.tags) out.add(t)
  }
  return [...out]
}
